import { alertBar } from "client_legacy/ndaw/alertBar";
import * as busyIndicator from "client_legacy/ndaw/busyIndicator";
import { localize } from "client_legacy/ndaw/culture";
import { HttpRequestError } from "client/Api/httpClient";
import * as windowFactory from "client_legacy/ndaw/windowFactory";
import { deviceHelper, documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { navigateTo, navigateToUriInPopOutWindow } from "client_legacy/ndaw/navigator";
import {
	EditorModes,
	OpenWacModel,
	OfficeDocumentEditor
} from "client/Api/Documents/common";
import { EditorWithItemFactory, getItemsInPreferredOrder } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import * as openInLocalAppAction from "./Actions/openInLocalAppAction";
import { previewImageInDialogAction } from "./Actions/previewImageInDialogAction";
import { downloadDocumentInline } from "./Actions/downloadAction";
import {
	DocumentItemType,
	OfficeWebAppOpenTarget,
	OneNoteNotebookViewModel,
	FileViewModel,
	DocumentItemViewModel,
	RecentDocumentItemViewModel
} from "client/Api/Documents/SharePoint/common";
import { DocumentError } from "client/Api/Documents/SharePoint/sharepointFileOpener";
import { convertToLocationModel } from "./listLocationModelProvider";
import * as documentOpenPossibilities from "./documentOpenPossibilities";
import { getOpenLocalizations } from "./documentOpenPossibilities";
import { getDownloadFileUrl } from "./UrlsProviders/documentsItemUrlsProviders";
import {
	getSharePointLocationUrl,
	getUrlToOpenDocumentInNewWorkspaceTab
} from "client/Features/Documents/routing";
import { openDocumentWindow } from "client/Features/Documents/DocumentWindow";
import { openAlertDialog } from "client/Components";
import { officeInterfaceEditorModel, sharepointViewerUrl } from "client/Api/Documents/SharePoint/sharepointFileEditor";
import { ContainerType } from "client_legacy/ndaw/busyIndicator";
import { navigateToWorkspace } from "client/Features/Workspace/routing";

function openDocument(document: DocumentItemViewModel) {
	if (document.itemType === DocumentItemType.Folder) {
		const dmsUrl: string = getSharePointLocationUrl(document.location, document.id);
		navigateTo(dmsUrl, false);
	} else {
		const {
			canBeOpenedInImagePreviewDialog,
			canBeOpenedInTheBrowser,
			canBeOpenedLocally,
			canBeOpenedOnline,
			canBeOpenedInSharePointViewer
		} = documentOpenPossibilities.calculate(document);

		const editorFactories: EditorWithItemFactory<() => void>[] = [
			[
				OfficeDocumentEditor.Online,
				() => {
					if (!canBeOpenedOnline) {
						return null;
					}

					return () => openFileInWopi(document);
				}
			],
			[
				OfficeDocumentEditor.Local,
				() => {
					if (!canBeOpenedLocally) {
						return null;
					}

					return () => {
						if (canBeOpenedOnline) {
							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								getOpenLocalizations(document),
								() => openFileInWopi(document)
							);
						}

						openInLocalAppAction.open(document);
					};
				}
			]
		];

		const orderedEditors: (() => void)[] = getItemsInPreferredOrder(
			documentsConfiguration().sharePoint.defaultOfficeDocumentEditor,
			editorFactories
		);

		if (orderedEditors.length > 0) {
			orderedEditors[0]();
		} else if (canBeOpenedInSharePointViewer) {
			if (document.itemType !== DocumentItemType.File) {
				throw new Error("The document is not a file.");
			}

			openFileInSharePointViewer(document);
		} else if (canBeOpenedInImagePreviewDialog) {
			previewImageInDialogAction.execute(document, [document]);
		} else if (canBeOpenedInTheBrowser) {
			downloadDocumentInline(document);
		} else {
			console.error(`We don't have any way we support opening document '${JSON.stringify(document)}'`);
			alertBar.showTextAlert(localize("_GeneralErrorMessage"), "error");
		}
	}
}

async function openFileInWopi(
	fileModel: FileViewModel | OneNoteNotebookViewModel | RecentDocumentItemViewModel,
	closeCallback?: () => void,
	hideRibbon?: boolean,
	editorMode?: EditorModes
) : Promise<void> {
	//If the hideRibbon parameter is not passed or not defined
	//automatically set it to false so the file ribbon will be shown
	if (hideRibbon === undefined) {
		hideRibbon = false;
	}

	const { sharePoint: { wopi: { openTarget } } } = documentsConfiguration();

	if (openTarget === OfficeWebAppOpenTarget.NewWorkspaceTab) {
		const url: string = getUrlToOpenDocumentInNewWorkspaceTab(fileModel.id, fileModel.location, editorMode);
		windowFactory.open(url);
		return;
	}

	const busyIndicatorContainer: ContainerType = "body";
	busyIndicator.show(busyIndicatorContainer);

	let openWacModel: OpenWacModel;
	try {
		openWacModel = await officeInterfaceEditorModel(
			convertToLocationModel(fileModel.location),
			fileModel.id,
			editorMode,
			deviceHelper.wopi
				.areAsyncRequestsAllowed(openTarget)
		);
	} catch (e) {
		let error: DocumentError | null = null;
		if (e instanceof HttpRequestError) {
			try {
				const { error: retrievedError } = e.readJson<{ error: DocumentError; }>();
				error = retrievedError;
			} catch (e) {
				console.error(e);
			}
		}

		switch (error) {
			case DocumentError.FileUnavailable:
				showFileUnavailablePopup();
				break;
			case DocumentError.LibraryUnavailable:
				showLibraryUnavailablePopup();
				break;
			default:
				showGeneralErrorDialog();
				break;
		}

		return;
	} finally {
		busyIndicator.hide(busyIndicatorContainer);
	}

	if (!openWacModel || openWacModel.url === "") {
		// Can't download onenote notebooks
		if (fileModel.itemType === DocumentItemType.OneNoteNotebook) {
			showGeneralErrorDialog();
			return;
		}

		// Download the file (no editor url is found)
		navigateTo(getDownloadFileUrl(fileModel.id, fileModel.location));
		return;
	}

	if (openTarget === OfficeWebAppOpenTarget.NewOffice365Tab) {
		windowFactory.open(openWacModel.url);
		return;
	}

	openDocumentWindow({
		file: fileModel,
		url: openWacModel.url,
		version: openWacModel.version,
		shouldHideRibbon: hideRibbon,
		editorMode: openWacModel.editorMode,
		onClose: closeCallback,
		isResizable: deviceHelper.wopi.allowResizing()
	});
}

function showFileUnavailablePopup() {
	openAlertDialog({
		title: localize("_FileUnavailableTitle"),
		children: localize("_FileUnavailableMessage"),
		confirmText: localize("_Close"),
		showCloseButton: true,
		trackingId: "sp-file-unavailable-dialog"
	});
}

function showGeneralErrorDialog() {
	openAlertDialog({
		title: localize("_Error"),
		children: localize("_GeneralErrorMessage"),
		confirmText: localize("_Ok"),
		trackingId: "sp-general-error-dialog"
	});
}

function showLibraryUnavailablePopup() {
	openAlertDialog({
		title: localize("_LibraryUnavailableTitle"),
		children: localize("_LibraryUnavailableMessage"),
		confirmText: localize("_BackToWorkspace"),
		trackingId: "sp-glibrary-unavailable-dialog"
	})
		.then(() => navigateToWorkspace());
}

async function openFileInSharePointViewer(
	fileModel: FileViewModel | RecentDocumentItemViewModel,
): Promise<void> {
	const {
		id: fileId,
		location
	} = fileModel;

	const busyIndicatorContainer: ContainerType = "body";
	busyIndicator.show(busyIndicatorContainer);

	try {
		const sharePointUrl: string = await sharepointViewerUrl(
			convertToLocationModel(location),
			fileId,
		);

		if (!sharePointUrl || sharePointUrl === "") {

			// Download the file (no editor url is found)
			navigateTo(getDownloadFileUrl(fileModel.id, fileModel.location));
			return;
		}

		navigateToUriInPopOutWindow(sharePointUrl);
	} catch(e) {
		console.error(e);

		showGeneralErrorDialog();
	} finally {
		busyIndicator.hide(busyIndicatorContainer);
	}

}

export {
	openDocument,
	openFileInWopi,
	openFileInSharePointViewer
};
